import {
  IOfferingRes,
  IPriceUnderProductSchemaUI,
  IQuoteItemReqSchema,
  IQuoteOfferingReqSchema,
  IQuoteOfferingRespSchema,
  IQuotePrice,
  IQuoteTemplateConfigSchema,
  IRateResSchema,
} from '@monetize/types/app';
import { Control, UseFormSetError, UseFormSetValue } from 'react-hook-form';
import { arrayToObject } from '../../../../../utils/misc';
import QuoteItem from './QuoteItem';

export interface QuoteItemsProps {
  offering?: IOfferingRes;
  parentQuoteOffering?: IQuoteOfferingRespSchema | null;
  offeringRate: IRateResSchema | null;
  quoteOffering?: IQuoteOfferingRespSchema | null;
  priorScheduledQuoteOffering?: IQuoteOfferingRespSchema | null;
  rateId: string | null | undefined;
  quoteItems?: IQuoteItemReqSchema[];
  watchItems?: IQuoteItemReqSchema[];
  control: Control<IQuoteOfferingReqSchema>;
  isOpen: boolean;
  isRemoved?: boolean;
  isReadOnly?: boolean;
  quotePrices: IQuotePrice[];
  displayConfig?: IQuoteTemplateConfigSchema | null;
  isQOAddedToAmendment?: boolean;
  isDisabled: boolean;
  orderedPricesUnderProduct: IPriceUnderProductSchemaUI[];
  setValue: UseFormSetValue<IQuoteOfferingReqSchema>;
  setError: UseFormSetError<IQuoteOfferingReqSchema>;
  handleQuoteOffering: () => void;
  handleQuoteOfferingWithoutDirtyCheck: () => void;
}

/**
 * Render all quote items for a given offering
 */
export const QuoteItems = ({
  offering,
  parentQuoteOffering,
  offeringRate,
  quoteOffering,
  priorScheduledQuoteOffering,
  rateId,
  quoteItems,
  watchItems,
  control,
  isOpen,
  isRemoved,
  isReadOnly,
  quotePrices,
  displayConfig,
  isQOAddedToAmendment,
  isDisabled,
  orderedPricesUnderProduct,
  setValue,
  setError,
  handleQuoteOffering,
  handleQuoteOfferingWithoutDirtyCheck,
}: QuoteItemsProps) => {
  if (
    !offering ||
    !quoteOffering ||
    !rateId ||
    !quoteItems?.length ||
    !isOpen
  ) {
    return null;
  }

  const quoteItemsById = arrayToObject(quoteOffering.items, 'id');
  const priorScheduledQuoteItemsByProductId = arrayToObject(
    priorScheduledQuoteOffering?.items || [],
    'productId',
  );
  const watchItemIndexById =
    watchItems != null
      ? watchItems.reduce<Record<string, number>>((acc, wi, i) => {
          if (wi.id) {
            acc[wi.id] = i;
          }
          return acc;
        }, {})
      : null;

  return (
    <>
      {quoteItems.map((quoteItem, sortedIndex) => {
        if (!quoteItem.id || !quoteItemsById[quoteItem.id]) {
          return null;
        }

        const formIndex =
          watchItemIndexById != null && quoteItem.id != null
            ? (watchItemIndexById[quoteItem.id] ?? -1)
            : sortedIndex;
        const index = formIndex >= 0 ? formIndex : sortedIndex;

        return (
          <QuoteItem
            key={`${quoteOffering.id}-${quoteItem.id}`}
            id={quoteItem.id}
            index={index}
            quoteOfferingId={quoteOffering.id}
            parentQuoteOfferingId={parentQuoteOffering?.id}
            item={quoteItem}
            itemFromServer={quoteItemsById[quoteItem.id]}
            priorScheduledQuoteItem={
              priorScheduledQuoteItemsByProductId[quoteItem.productId!]
            }
            offering={offering}
            offeringRate={offeringRate}
            control={control}
            isRemoved={isRemoved}
            isReadOnly={isReadOnly}
            setValue={setValue}
            setError={setError}
            watchRateId={rateId}
            quotePrices={quotePrices}
            handleQuoteOffering={handleQuoteOffering}
            handleQuoteOfferingWithoutDirtyCheck={
              handleQuoteOfferingWithoutDirtyCheck
            }
            displayConfig={displayConfig}
            isQOAddedToAmendment={isQOAddedToAmendment}
            isDisabled={isDisabled}
            orderedPricesUnderProduct={orderedPricesUnderProduct}
          />
        );
      })}
    </>
  );
};

export default QuoteItems;
